// src/profile/username.ts
//
// Username reservation, claim, release, lookup.
//
// Reservation collection /usernames/{username_lower} stores
// { uid: string, claimed_at: Timestamp }. Existence of a doc means
// the username is taken. Atomic claim is enforced via security
// rules:
//   • CREATE allowed only when the doc doesn't exist (rule's
//     `request.resource` check + the "no existing resource" path)
//   • The new doc's `uid` must equal request.auth.uid
//   • DELETE allowed only when resource.data.uid == request.auth.uid
//
// Claim flow (atomic at the rules layer; no transactions needed):
//   1. Validate format client-side.
//   2. Try to write /usernames/{lower}. Fails with permission-denied
//      if taken — that IS the uniqueness check.
//   3. On success, write user_profile with username + lower.
//   4. Best-effort delete of any previous reservation owned by uid
//      (release old name).
//
// Why /usernames/{lower} and not a queryable index on /users:
// rules can't enforce "only one doc with this username_lower exists"
// across a query — but they CAN enforce "this exact doc id is unique
// because it doesn't exist yet." Using the lowered username as the
// doc id is the standard Firestore pattern for global uniqueness.

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  deleteDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { validateUsername, USERNAME_CHANGE_COOLDOWN_DAYS } from './types';

export type ClaimUsernameResult =
  | { ok: true; username: string }
  | { ok: false; reason: 'invalid' | 'taken' | 'too_soon' | 'unauthenticated' | 'unknown'; detail?: string };

/**
 * Claim a username for the current user. Atomic — the create either
 * succeeds (username is yours) or fails with permission-denied
 * (someone got there first). Never partially succeeds.
 *
 * If the user already has a username, this releases the old
 * reservation after the new one is claimed. We do new-claim FIRST
 * so a failed claim doesn't strand the user without any name.
 */
export async function claimUsername(
  desired: string
): Promise<ClaimUsernameResult> {
  const user = auth.currentUser;
  if (!user) return { ok: false, reason: 'unauthenticated' };

  const validation = validateUsername(desired);
  if (validation.ok !== true) {
    // Avoid relying on `!validation.ok` narrowing — under strict:false
    // TS doesn't narrow union members reliably. Cast to the failure
    // shape explicitly.
    const failure = validation as { ok: false; reason: string };
    return { ok: false, reason: 'invalid', detail: failure.reason };
  }
  const lower = validation.normalized;

  // Cooldown check: if user has username_changed_at within the
  // last cooldown window, refuse. This is best-effort client-side;
  // Cloud Function can re-enforce in a callable wrapper later.
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data() as any;
      const last = data.username_changed_at as Timestamp | undefined;
      if (last) {
        const ageDays =
          (Date.now() - last.toDate().getTime()) / (24 * 60 * 60 * 1000);
        if (ageDays < USERNAME_CHANGE_COOLDOWN_DAYS) {
          return {
            ok: false,
            reason: 'too_soon',
            detail: `${Math.ceil(USERNAME_CHANGE_COOLDOWN_DAYS - ageDays)}`,
          };
        }
      }

      // If they already own this exact username, no-op success.
      if (data.username_lower === lower) {
        return { ok: true, username: data.username || desired };
      }
    }
  } catch (err) {
    console.warn('claimUsername: cooldown check failed (continuing):', err);
  }

  // Atomic claim: create /usernames/{lower}. Rules require !exists()
  // and uid match; permission-denied here means another user holds
  // the name (could also mean rule misconfig — same response either
  // way: the user's name didn't get claimed).
  const reservationRef = doc(db, 'usernames', lower);
  try {
    await setDoc(reservationRef, {
      uid: user.uid,
      claimed_at: serverTimestamp(),
    });
  } catch (err: any) {
    const code = err?.code as string | undefined;
    if (code === 'permission-denied' || code === 'already-exists') {
      return { ok: false, reason: 'taken' };
    }
    return { ok: false, reason: 'unknown', detail: err?.message };
  }

  // Read previous username (if any) so we can release after claim.
  let previousLower: string | undefined;
  try {
    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      previousLower = (userSnap.data() as any).username_lower;
    }
  } catch (err) {
    console.warn('claimUsername: couldnt read previous username:', err);
  }

  // Update user profile with new name + lower copy + change ts.
  try {
    const userRef = doc(db, 'users', user.uid);
    await setDoc(
      userRef,
      {
        username: lower,
        username_lower: lower,
        username_changed_at: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn('claimUsername: profile write failed (orphan reservation):', err);
    // Try to release the reservation we just created so we don't
    // leave it orphaned. Best effort.
    try {
      await deleteDoc(reservationRef);
    } catch {
      /* ignore */
    }
    return { ok: false, reason: 'unknown', detail: 'profile_write_failed' };
  }

  // Release previous reservation if any (and it's not the same).
  if (previousLower && previousLower !== lower) {
    try {
      await deleteDoc(doc(db, 'usernames', previousLower));
    } catch (err) {
      // Non-fatal — user has the new name; old name will free up
      // if they ever try to release it again, or via a sweep.
      console.warn('claimUsername: previous-release failed:', err);
    }
  }

  return { ok: true, username: lower };
}

/**
 * Look up a username (case-insensitive) → uid. Used to resolve
 * /u/@{username} URLs.
 */
export async function lookupUsername(username: string): Promise<string | null> {
  const lower = username.trim().toLowerCase();
  if (!lower) return null;
  try {
    const snap = await getDoc(doc(db, 'usernames', lower));
    if (!snap.exists()) return null;
    return (snap.data() as any).uid as string;
  } catch (err) {
    console.warn('lookupUsername failed:', err);
    return null;
  }
}

/**
 * Release the current user's username reservation. Used during
 * account deletion or explicit "drop my username" flows.
 */
export async function releaseMyUsername(): Promise<boolean> {
  const user = auth.currentUser;
  if (!user) return false;
  try {
    const userSnap = await getDoc(doc(db, 'users', user.uid));
    if (!userSnap.exists()) return true;
    const lower = (userSnap.data() as any).username_lower as string | undefined;
    if (!lower) return true;
    await deleteDoc(doc(db, 'usernames', lower));
    await updateDoc(doc(db, 'users', user.uid), {
      username: null,
      username_lower: null,
    });
    return true;
  } catch (err) {
    console.warn('releaseMyUsername failed:', err);
    return false;
  }
}
